/**
 * placementBounds.ts — PLAT-10921 step 3 (epic decision E7).
 *
 * The scope a pinned graphic lives inside, and the timeline target each pinned
 * placement resolves to.
 *
 * > **Cards push in: you start when the start card stops, and you end when the
 * > end card starts.**
 *
 * | Bound       | Value                                                  | Fallback with no card    |
 * | ----------- | ------------------------------------------------------ | ------------------------ |
 * | Scope start | the intro card's **full** `end_time`, overlap included  | `0`                      |
 * | Scope end   | the end card's `time` — the moment it **starts**        | the content end          |
 *
 * Symmetric: neither bound reaches into a card's overlap region.
 *
 * Everything here is TIMELINE (`time` / `duration`). Canvas position and size
 * are a different axis and never appear.
 */

import {
  findIntroCardScopeEnd,
  isEndCardMeta,
  readPlacementMeta,
} from '@src/utility/extraElementData';
import { LogManager } from '@src/utility/Logger';

import { Element } from '../models/Element';
import { CompositionElement } from '../models/elements/CompositionElement';
import {
  isCardPlacement,
  isPinnablePlacement,
  PLACEMENT,
} from './placementEvaluation';

import type { Track } from '../models/Track';

const logger = LogManager.getLogger('editor:placementBounds');

export interface TimelineScopeBounds {
  /** Where a pinned graphic may start. */
  start: number;
  /** Where a pinned graphic must end. */
  end: number;
}

/**
 * The end time of all content on the timeline, excluding the given elements.
 *
 * Promoted from `EndCardManager` (was private) — it is the scope's tail for
 * every placement, not just end cards.
 *
 * **Coupled logos are skipped deliberately.** Their `time` / `duration` in the
 * model are stale at reconcile time: coupling is applied at *serialization*
 * (`Timeline.adjustCoupledElementsToTrackBounds` clones and adjusts) and pushed
 * back into the model afterwards by `reapplyCoupledElementBounds`. Reading them
 * here would bound the scope by a value from the previous pass. See the ordering
 * note on `PlacementReconciler.reconcile` (DS3).
 */
export function getContentEndTime(
  tracks: readonly Track[],
  excludeElementIds: ReadonlySet<string>,
): number {
  let maxEndTime = 0;

  for (const track of tracks) {
    for (const element of track.elements) {
      if (excludeElementIds.has(element.id)) continue;
      if (Element.isLogoElement(element) && element.isCoupledToTrack()) {
        continue;
      }
      if (element.end_time > maxEndTime) maxEndTime = element.end_time;
    }
  }

  return maxEndTime;
}

/**
 * The earliest end card's start time — the moment content has to be over by.
 * `null` when the timeline has no end card.
 */
export function findEndCardScopeStart(
  tracks: readonly Track[],
  extraData: Record<string, unknown>,
): number | null {
  let earliest: number | null = null;

  for (const track of tracks) {
    for (const element of track.elements) {
      if (!(element instanceof CompositionElement)) continue;
      if (!isEndCardMeta(extraData[element.id])) continue;
      if (earliest === null || element.time < earliest) earliest = element.time;
    }
  }

  return earliest;
}

/** Every end card on the timeline, by id — excluded from the content end. */
export function findEndCardIds(
  tracks: readonly Track[],
  extraData: Record<string, unknown>,
): Set<string> {
  const ids = new Set<string>();
  for (const track of tracks) {
    for (const element of track.elements) {
      if (!(element instanceof CompositionElement)) continue;
      if (isEndCardMeta(extraData[element.id])) ids.add(element.id);
    }
  }
  return ids;
}

/**
 * Every pinned composition, by id — the **followers**, which must never count
 * toward the content end that defines their own bounds.
 *
 * Without this the dependency is circular: a pinned `clip` graphic currently
 * running past the content raises the content end, which pushes the end card
 * out, which moves the scope end, which re-stretches the graphic. It never
 * converges, and it drags the end card along with it.
 *
 * The same reasoning already excludes end cards (followers of the content end)
 * and coupled logos (followers of their track): a derived extent is not content,
 * and at reconcile time it is a value from the previous pass.
 */
export function findPinnedFollowerIds(
  tracks: readonly Track[],
  extraData: Record<string, unknown>,
): Set<string> {
  const ids = new Set<string>();
  for (const track of tracks) {
    for (const element of track.elements) {
      if (!(element instanceof CompositionElement)) continue;
      const meta = readPlacementMeta(extraData[element.id]);
      if (meta?.pinned && isPinnablePlacement(meta.placement)) {
        ids.add(element.id);
      }
    }
  }
  return ids;
}

/**
 * Everything that must NOT count as content when deriving a placement's bounds:
 * every end card (which defines the scope end rather than following it) and every
 * pinned follower (whose extent is itself derived).
 *
 * One function so the policy lives in one place — the reconciler and the insert
 * path both need exactly this set, and a graphic that lands somewhere the next
 * reconcile immediately moves it away from is a visible jump.
 *
 * @param alsoExclude the element currently being placed, when there is one.
 */
export function resolvePlacementExclusions(
  tracks: readonly Track[],
  extraData: Record<string, unknown>,
  alsoExclude?: string,
): Set<string> {
  const excluded = new Set([
    ...findEndCardIds(tracks, extraData),
    ...findPinnedFollowerIds(tracks, extraData),
  ]);
  if (alsoExclude) excluded.add(alsoExclude);
  return excluded;
}

/**
 * The card-bounded scope, from the element's own point of view.
 *
 * `excludeElementIds` must include the element being placed: a graphic whose
 * own end runs past the content would otherwise define the very bound it is
 * about to be snapped to. Use `resolvePlacementExclusions` to build it.
 */
export function resolveTimelineScopeBounds(
  tracks: readonly Track[],
  extraData: Record<string, unknown>,
  excludeElementIds: ReadonlySet<string>,
): TimelineScopeBounds {
  const introEnd = findIntroCardScopeEnd(tracks, extraData);
  const endCardStart = findEndCardScopeStart(tracks, extraData);
  const contentEnd = getContentEndTime(tracks, excludeElementIds);

  const start = introEnd ?? 0;
  const end = endCardStart ?? contentEnd;

  // A degenerate scope (an end card sitting before the intro card ends) would
  // resolve to a negative duration. Collapse instead of inverting.
  return { start, end: Math.max(start, end) };
}

/**
 * A pinned placement's timeline target. `null` on an edge means that edge is
 * not enforced by this placement and keeps whatever value it has.
 */
export interface PinnedTimelineTarget {
  time: number | null;
  endTime: number | null;
}

export interface DerivePinnedTimelineTargetParams {
  placement: string;
  /**
   * Used only to refuse the one invalid combination: a scope-defining CARD
   * pinned to the scope it defines.
   */
  compositionType: string | null;
  scope: TimelineScopeBounds;
  /** The content end excluding this element and every end card. */
  contentEndTime: number;
  /** The card's own transition overlap. */
  overlapBefore: number;
}

/**
 * Which timeline values a pinned placement resolves to.
 *
 * **Cards are positioned relative to the content; graphics follow the clip's
 * bounds.** `start` / `end` put a card OUTSIDE the clip and are unchanged from
 * before this ticket. `clip-start` / `clip-end` pin a graphic to where the clip's
 * content begins and ends — the bounds the cards push in to.
 *
 * `selection` is absent on purpose: its range comes from the anchored words, not
 * from the scope (E4), and is derived by the reconciler from the transcript.
 */
export function derivePinnedTimelineTarget({
  placement,
  compositionType,
  scope,
  contentEndTime,
  overlapBefore,
}: DerivePinnedTimelineTargetParams): PinnedTimelineTarget | null {
  // A card that DEFINES a scope bound cannot be pinned to that scope: an intro
  // card is the scope start and an end card is the scope end, so `clip*` on
  // either is self-referential and would march the card down the timeline. Warn
  // and leave it alone rather than honouring a misauthored template.
  if (
    (compositionType === 'introCard' || compositionType === 'endCard') &&
    !isCardPlacement(placement)
  ) {
    logger.warn(
      'Ignoring a clip-bound placement on a scope-defining card — use start/end for cards',
      { compositionType, placement },
    );
    return null;
  }

  switch (placement) {
    case PLACEMENT.CLIP:
      // Semantically identical to logo coupling ("span these bounds and keep
      // spanning them"), which `Timeline.adjustCoupledElementsToTrackBounds`
      // implements at serialization time for a different subsystem. Coupled
      // logos are deliberately NOT folded in here — see the plan's Out of scope.
      return { time: scope.start, endTime: scope.end };

    case PLACEMENT.CLIP_START:
      // Begins where the clip's content begins — after the intro card has fully
      // played — and keeps its own duration.
      return { time: scope.start, endTime: null };

    case PLACEMENT.CLIP_END:
      // Ends where the clip's content ends — where the end card starts. The
      // START is left free, so `duration` absorbs the change.
      return { time: null, endTime: scope.end };

    case PLACEMENT.START:
      // CARD, before the clip. Unchanged from before this ticket: the insert puts
      // it at the timeline start with a ripple, and that is where it stays.
      return { time: 0, endTime: null };

    case PLACEMENT.END:
      // CARD, after the clip. Unchanged from before this ticket — this is
      // verbatim what `EndCardManager.reconcileEndCards` did: start at the
      // content end, minus the overlap it transitions in over.
      return {
        time: Math.max(0, contentEndTime - overlapBefore),
        endTime: null,
      };

    default:
      return null;
  }
}
